// src/pages/audittrail/AuditExplorerPage.tsx
// Compliance-grade audit trail explorer — Records (default), Verify,
// Retention, Compliance, DSAR. Entirely admin-gated (AdminRoute, in App.tsx).
// See docs/ux/audittrail-ia.md.

import { useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { RecordsTab } from '@/features/audittrail/RecordsTab';
import { VerifyTab } from '@/features/audittrail/VerifyTab';
import { RetentionTab } from '@/features/audittrail/RetentionTab';
import { ComplianceTab } from '@/features/audittrail/ComplianceTab';
import { DsarTab } from '@/features/audittrail/DsarTab';

type Tab = 'records' | 'verify' | 'retention' | 'compliance' | 'dsar';

export const AuditExplorerPage = observer(() => {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>('records');

  const tabs: { id: Tab; label: string }[] = [
    { id: 'records', label: t('audittrail.tabs.records') },
    { id: 'verify', label: t('audittrail.tabs.verify') },
    { id: 'retention', label: t('audittrail.tabs.retention') },
    { id: 'compliance', label: t('audittrail.tabs.compliance') },
    { id: 'dsar', label: t('audittrail.tabs.dsar') },
  ];

  return (
    <div className="flex h-full flex-col space-y-6 p-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <h1 className="sr-only">{t('audittrail.page.title')}</h1>

      <div className="flex items-center gap-1 border-b border-border" role="tablist">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            type="button"
            role="tab"
            aria-selected={tab === tb.id}
            onClick={() => setTab(tb.id)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              tab === tb.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tb.label}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {tab === 'records' && <RecordsTab />}
        {tab === 'verify' && <VerifyTab />}
        {tab === 'retention' && <RetentionTab />}
        {tab === 'compliance' && <ComplianceTab />}
        {tab === 'dsar' && <DsarTab />}
      </div>
    </div>
  );
});

export default AuditExplorerPage;
